import { useAuditFeed, useCallCost, useCallUsage } from '@hostesa/api-client';
import { useAuth } from '@hostesa/auth-client';
import { useFlag } from '@hostesa/feature-flags';
import { useFormatters, useT } from '@hostesa/i18n';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  Input,
  Label,
  ListToolbar,
  LoadingState,
  PageHeader,
  Pagination,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@hostesa/ui';
import { Activity } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useHotelContext } from '../../lib/hotel-context';
import { AuditFeed } from './audit-feed';
import { namespace } from './i18n';
import { UsageSummary } from './usage-summary';

const PAGE_WRAP = 'flex flex-col gap-6';

function toNumber(value: string): number {
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * USAGE + AUDIT page.
 *
 * - Per-call usage ledger + rolled-up cost (looked up by call ID).
 * - Tenant-level usage summary rendered from the selected call's ledger, or a
 *   calm EmptyState when nothing is selected (the backend exposes per-call
 *   usage only — there is no tenant-wide rollup endpoint yet).
 * - A PII-REDACTED audit/activity feed (newest-first, hotelId-scoped for
 *   hotel-scoped admins). Rows are redacted by the backend; we never render raw
 *   PII or the redacted payload blobs.
 *
 * Guards: feature flag `usage_reporting`, RBAC `usage:read` / `audit:read`.
 */
export function UsagePage() {
  const { t } = useT(namespace);
  const { tenantId, hotelId, timezone, membership } = useHotelContext();
  const { can } = useAuth();
  const usageEnabled = useFlag('usage_reporting');
  const f = useFormatters(timezone);

  // Hotel-scoped admins must filter the audit feed to their hotel; tenant-wide
  // memberships (hotelId === null) may read the whole-tenant feed.
  const auditHotelId = membership.hotelId ? hotelId : undefined;

  const [callIdInput, setCallIdInput] = useState('');
  const [activeCallId, setActiveCallId] = useState('');

  // Audit feed filters/pagination (local state — the feed is an embedded section,
  // not a standalone list route).
  const [auditQ, setAuditQ] = useState('');
  const [auditOutcome, setAuditOutcome] = useState<'success' | 'failure' | 'blocked' | undefined>(
    undefined,
  );
  const [auditOffset, setAuditOffset] = useState(0);
  const [auditLimit, setAuditLimit] = useState(50);

  const canUsage = can('usage:read');
  const canAudit = can('audit:read');

  const usage = useCallUsage(tenantId, canUsage ? activeCallId : '');
  const cost = useCallCost(tenantId, canUsage ? activeCallId : '');
  const audit = useAuditFeed(canAudit ? tenantId : '', {
    hotelId: auditHotelId,
    q: auditQ || undefined,
    outcome: auditOutcome,
    limit: auditLimit,
    offset: auditOffset,
  });
  const auditItems = audit.data?.items ?? [];
  const auditTotal = audit.data?.total ?? 0;
  const auditFilterActive = Boolean(auditQ || auditOutcome);

  if (!usageEnabled) {
    return (
      <div data-testid="usage-page" className={PAGE_WRAP}>
        <PageHeader
          eyebrow={t('page.eyebrow')}
          title={t('page.title')}
          description={t('page.description')}
        />
        <EmptyState
          data-testid="usage-disabled-state"
          icon={<Activity />}
          title={t('disabled.title')}
          description={t('disabled.description')}
        />
      </div>
    );
  }

  if (!canUsage && !canAudit) {
    return (
      <div data-testid="usage-page" className={PAGE_WRAP}>
        <PageHeader
          eyebrow={t('page.eyebrow')}
          title={t('page.title')}
          description={t('page.description')}
        />
        <EmptyState
          data-testid="usage-no-access-state"
          title={t('noAccess.title')}
          description={t('noAccess.description')}
        />
      </div>
    );
  }

  const onLookup = (event: FormEvent) => {
    event.preventDefault();
    setActiveCallId(callIdInput.trim());
  };

  return (
    <div data-testid="usage-page" className={PAGE_WRAP}>
      <PageHeader
        eyebrow={t('page.eyebrow')}
        title={t('page.title')}
        description={t('page.description')}
      />

      {canUsage && (
        <Card data-testid="usage-cost-card">
          <CardHeader>
            <CardTitle>{t('cost.title')}</CardTitle>
            <CardDescription>{t('cost.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form
              data-testid="usage-cost-form"
              onSubmit={onLookup}
              className="flex items-end gap-3"
            >
              <div className="flex flex-col gap-1.5 flex-1">
                <Label htmlFor="usage-call-id-input">{t('cost.callIdLabel')}</Label>
                <Input
                  id="usage-call-id-input"
                  data-testid="usage-call-id-input"
                  value={callIdInput}
                  onChange={(e) => setCallIdInput(e.target.value)}
                  placeholder={t('cost.callIdPlaceholder')}
                />
              </div>
              <Button
                type="submit"
                data-testid="usage-lookup-btn"
                disabled={callIdInput.trim().length === 0}
              >
                {t('cost.lookupBtn')}
              </Button>
            </form>

            {activeCallId.length > 0 && cost.data && (
              <div
                className="flex items-center justify-between border-t border-[var(--color-border)] pt-3 mt-4"
                data-testid="usage-cost-total"
              >
                <span className="text-sm text-[var(--color-stone-mid)]">
                  {t('cost.totalLabel')}
                </span>
                <span className="font-[var(--font-mono)] text-sm font-medium text-[var(--color-charcoal)]">
                  {f.formatCurrency(toNumber(cost.data.totalCost), {
                    currency: cost.data.currency,
                  })}
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {canUsage &&
        (activeCallId.length === 0 ? (
          <Card data-testid="usage-summary-placeholder">
            <CardHeader>
              <CardTitle>{t('summary.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <EmptyState
                data-testid="usage-summary-placeholder-state"
                title={t('cost.emptyTitle')}
                description={t('cost.emptyDescription')}
              />
            </CardContent>
          </Card>
        ) : usage.isLoading ? (
          <LoadingState data-testid="usage-summary-loading" title={t('states.loading')} />
        ) : usage.isError ? (
          <ErrorState
            data-testid="usage-summary-error"
            title={t('states.errorTitle')}
            description={t('states.errorDescription')}
          />
        ) : (
          <UsageSummary entries={usage.data ?? []} cost={cost.data} timezone={timezone} />
        ))}

      {canAudit && (
        <div className="flex flex-col">
          <ListToolbar
            data-testid="audit-toolbar"
            searchValue={auditQ}
            onSearchChange={(v) => {
              setAuditOffset(0);
              setAuditQ(v);
            }}
            searchPlaceholder={t('audit.toolbar.searchPlaceholder')}
            trailing={
              auditFilterActive ? (
                <Button
                  data-testid="audit-clear-filters"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setAuditQ('');
                    setAuditOutcome(undefined);
                    setAuditOffset(0);
                  }}
                >
                  {t('audit.toolbar.clear')}
                </Button>
              ) : undefined
            }
          >
            <Select
              value={auditOutcome ?? 'all'}
              onValueChange={(v) => {
                setAuditOffset(0);
                setAuditOutcome(v === 'all' ? undefined : (v as 'success' | 'failure' | 'blocked'));
              }}
            >
              <SelectTrigger data-testid="audit-filter-outcome" className="h-8 w-40">
                <SelectValue placeholder={t('audit.toolbar.outcomeAll')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('audit.toolbar.outcomeAll')}</SelectItem>
                <SelectItem value="success">{t('audit.outcome.success')}</SelectItem>
                <SelectItem value="failure">{t('audit.outcome.failure')}</SelectItem>
                <SelectItem value="blocked">{t('audit.outcome.blocked')}</SelectItem>
              </SelectContent>
            </Select>
          </ListToolbar>

          {audit.isLoading ? (
            <LoadingState data-testid="audit-feed-loading" title={t('states.loading')} />
          ) : audit.isError ? (
            <ErrorState
              data-testid="audit-feed-error"
              title={t('states.auditErrorTitle')}
              description={t('states.auditErrorDescription')}
            />
          ) : auditItems.length === 0 && auditFilterActive ? (
            <EmptyState
              data-testid="audit-feed-empty-filtered"
              title={t('audit.emptyFilteredTitle')}
              description={t('audit.emptyFilteredDescription')}
            />
          ) : (
            <AuditFeed entries={auditItems} timezone={timezone} />
          )}

          {!audit.isLoading && !audit.isError && auditTotal > 0 && (
            <Pagination
              data-testid="audit-pagination"
              total={auditTotal}
              limit={auditLimit}
              offset={auditOffset}
              onChange={({ offset, limit }) => {
                setAuditOffset(offset);
                setAuditLimit(limit);
              }}
              rangeLabel={(from, to, total) => t('audit.paging.range', { from, to, total })}
              prevLabel={t('audit.paging.prev')}
              nextLabel={t('audit.paging.next')}
              pageSizeLabel={t('audit.paging.perPage')}
            />
          )}
        </div>
      )}
    </div>
  );
}
